/* eslint-disable no-unused-vars */
import {
  Button,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  Paper,
  styled,
  Typography
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StarRoundedIcon from "@material-ui/icons/StarRounded";
import { useQuery } from "@redux-requests/react";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Header, Loader, OverflowTip, VideoPreview } from "../../components";
import { deleteFavourite, getFavourites } from "../../ducks/actions";
import * as types from "../../ducks/types";
import { useActionRequest } from "../../hooks";

const useStyles = makeStyles(theme => ({
  gridRow: {
    justifyContent: "center",
    marginTop: "0.5rem",
    marginBottom: "0.5rem"
  },
  gridRowAlignStart: {
    justifyContent: "flex-start",
    cursor: "pointer"
  },
  gridCell: {
    padding: "1rem"
  },
  gridCellIcon: {
    display: "flex",
    padding: "1rem",
    justifyContent: "center"
  },
  progressCellContainer: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    [theme.breakpoints.down("md")]: { flexDirection: "column" }
  },
  previewCellContainer: {
    [theme.breakpoints.down("xs")]: { flexDirection: "column", textAlign: "center" }
  },
  previewImage: {
    [theme.breakpoints.up("sm")]: { marginRight: "1.5rem" }
  },

  checkIcon: {
    fontSize: "2rem"
  },
  previewImageLabel: {
    fontWeight: "600",
    fontSize: "1.2rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical"
  },
  actionsCellContainer: {
    display: "flex",
    flexDirection: "column",
    width: "9rem"
  },
  mandatoryLabel: {
    marginTop: "1rem",
    color: "red"
  }
}));

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 10,
  borderRadius: 5,
  width: "10rem"
}));

const FavouritesPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFavourites());
  }, [dispatch]);

  const {
    data: { data: favourites },
    loading
  } = useQuery({ type: types.GET_FAVOURITES, defaultData: { data: [] } });

  const courses = favourites
    .filter(({ favouritable }) => favouritable?.type === "course")
    .map(c => ({
      ...c.favouritable,
      favouriteId: c.id
    }));
  const modules = favourites
    .filter(({ favouritable }) => favouritable?.type === "course_module")
    .map(cm => ({
      ...cm.favouritable,
      favouriteId: cm.id
    }));

  const openCourse = courseId => () => history.push(`courses/${courseId}`);
  const openModule = moduleId => () => history.push(`course_modules/${moduleId}`);

  const deleteFavouriteRequest = useActionRequest({
    action: deleteFavourite
  });

  const onDeleteFavourite = id => event => {
    event.stopPropagation();
    deleteFavouriteRequest({ id });
  };

  return (
    <>
      <Header
        icon="star"
        title={intl.formatMessage({
          id: "Pages.FavouritesPage.Favourites",
          defaultMessage: "Favourites"
        })}
      />
      <Loader loading={loading}>
        <Grid container className={classes.gridContainer}>
          {courses.map(value => (
            <Paper
              key={value.id}
              elevation={0}
              alignItems="center"
              component={Grid}
              className={`${classes.gridRow} ${classes.gridRowAlignStart}`}
              container
              item
              onClick={openCourse(value.id)}
            >
              <Grid className={classes.gridCellIcon} item xs={12} sm={1}>
                <IconButton onClick={onDeleteFavourite(value.favouriteId)}>
                  <StarRoundedIcon className={classes.checkIcon} color="primary" />
                </IconButton>
              </Grid>
              <Grid className={classes.gridCell} item xs={12} sm={8} lg={6}>
                <Typography variant="body1">
                  <strong>{value.name}</strong>
                </Typography>
                <Typography variant="body1">
                  {`${value.courseModules?.length} ${intl.formatMessage({
                    id: "Pages.Courses.Modules",
                    defaultMessage: "modules"
                  })} - ${value.totalTime} ${intl.formatMessage({
                    id: "Pages.Courses.TotalHours",
                    defaultMessage: "total hours"
                  })}`}
                </Typography>
              </Grid>
              <Grid className={classes.gridCell} item xs={12} sm={3} lg={3}>
                <div className={classes.progressCellContainer}>
                  <Typography variant="body2">
                    {`${intl.formatMessage({
                      id: "Pages.Courses.Viewed",
                      defaultMessage: "viewed"
                    })}: ${value.completionPercentage}%`}
                  </Typography>
                  <span>
                    <BorderLinearProgress
                      variant="determinate"
                      value={value.completionPercentage}
                    />
                  </span>
                  {value.completionPercentage === 100 && (
                    <CheckCircleIcon className={classes.checkIcon} color="primary" />
                  )}
                </div>
              </Grid>
            </Paper>
          ))}
          {modules?.map(value => {
            return (
              <Paper
                elevation={0}
                key={value.id}
                alignItems="center"
                component={Grid}
                className={classes.gridRow}
                container
                item
              >
                <Grid className={classes.gridCellIcon} item xs={12} sm={1}>
                  <IconButton onClick={onDeleteFavourite(value.favouriteId)}>
                    <StarRoundedIcon className={classes.checkIcon} color="primary" />
                  </IconButton>
                </Grid>
                <Grid
                  container
                  item
                  wrap="nowrap"
                  alignItems="center"
                  className={`${classes.gridCell} ${classes.previewCellContainer}`}
                  xs={12}
                  sm={8}
                  lg={6}
                >
                  {value.coverImage ? (
                    <img
                      className={classes.previewImage}
                      alt=""
                      width={200}
                      src={value.coverImage}
                    />
                  ) : (
                    <VideoPreview
                      className={classes.previewImage}
                      imageWidth={200}
                      videoUrl={value?.recording?.videoUrl}
                      thumbUrl={value?.document?.pdf?.previewUrl}
                    />
                  )}

                  <OverflowTip
                    textToDisplay={value.title}
                    className={classes.previewImageLabel}
                    textVariant="h6"
                  />
                </Grid>
                <Grid className={classes.gridCell} item xs={12} sm={3}>
                  <div className={classes.progressCellContainer}>
                    <Typography variant="body2">
                      {`${intl.formatMessage({
                        id: "Pages.Course.Viewed",
                        defaultMessage: "viewed"
                      })}: ${value?.completionPercentage}%`}
                    </Typography>
                    <span>
                      <BorderLinearProgress
                        variant="determinate"
                        value={value?.completionPercentage}
                      />
                    </span>
                    {value.completionPercentage === 100 && (
                      <CheckCircleIcon className={classes.checkIcon} color="primary" />
                    )}
                  </div>
                </Grid>
                <Grid className={classes.gridCell} container item justify="center" xs={12} lg={2}>
                  <div className={classes.actionsCellContainer}>
                    <Button onClick={openModule(value.id)} color="primary" variant="contained">
                      {intl.formatMessage({
                        id: "Pages.Course.GoToModule",
                        defaultMessage: "Go to module"
                      })}
                    </Button>
                    <Typography className={classes.mandatoryLabel} variant="body2">
                      {value?.mandatory &&
                        `* ${intl.formatMessage({
                          id: "Pages.Course.RequiredModule",
                          defaultMessage: "Required module"
                        })}`}
                    </Typography>
                  </div>
                </Grid>
              </Paper>
            );
          })}
        </Grid>
      </Loader>
    </>
  );
};

export default FavouritesPage;
